import {
  Component,
  OnChanges,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  HostListener,
} from '@angular/core';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

import {IUserGuideActiveStep} from '../../shared/user-guide.model';
import {UserGuideService} from '../../shared/user-guide.service';


interface ResizeObserverEntry {
  readonly target: Element;
  readonly contentRect: DOMRectReadOnly;
}

type ResizeObserverCallback = (entries: ResizeObserverEntry[], observer: ResizeObserver) => void;

declare class ResizeObserver {
  constructor(callback: ResizeObserverCallback);
  observe(target: Element): void;
  unobserve(target: Element): void;
  disconnect(): void;
}

@Component({
  selector: 'app-user-guide-overlay',
  templateUrl: './user-guide-overlay.component.html',
  styleUrls: ['./user-guide-overlay.component.scss'],
})
export class UserGuideOverlayComponent implements OnChanges, OnInit, OnDestroy {
  @Input() activeStep: IUserGuideActiveStep;

  clipPath: string;
  showOverlay: boolean;

  private destroyer$: Subject<void> = new Subject();
  private resizeObserver: ResizeObserver;

  constructor(
    private cdr: ChangeDetectorRef,
    private userGuideService: UserGuideService
  ) {}

  @HostListener('window:click', ['$event'])
  onElementClick($event: MouseEvent) {
    this.userGuideService.refreshOverlay$.next();
  }

  ngOnInit(): void {
    this.userGuideService.refreshOverlay$
      .pipe(takeUntil(this.destroyer$))
      .subscribe(() => {
        this.getCoverPolygon();
        this.cdr.detectChanges();
      });

    this.initResizeObserver();
  }

  ngOnDestroy(): void {
    this.destroyer$.next();
    this.destroyer$.unsubscribe();
    this.resizeObserver.disconnect();
  }

  ngOnChanges(): void {
    this.getCoverPolygon();

    if (!this.resizeObserver) {
      this.initResizeObserver();
    }
    this.observeElementResize();
  }

  private initResizeObserver() {
    if (!this.resizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        this.getCoverPolygon();
        this.cdr.detectChanges();
      });

      this.observeElementResize();
    }
  }

  private observeElementResize() {
    const element = this.userGuideService.getElement(this.activeStep.step);
    if (this.resizeObserver && element) {
      this.resizeObserver.observe(element);
    }
  }

  private getCoords(): DOMRect | null {
    if (!this.activeStep.step.element) {
      return null;
    }

    const el: HTMLElement = this.userGuideService.getElement(
      this.activeStep.step
    );

    if (!el) {
      return null;
    }

    return el.getBoundingClientRect() as DOMRect;
  }

  private getCoverPolygon() {
    const coords: DOMRect = this.getCoords();
    if (!coords) {
      this.showOverlay = false;
      return;
    }

    const padding = 4;
    this.showOverlay = true;
    this.clipPath = `polygon(
          0% 0%,
          0% 100%,
          ${coords.left - padding}px 100%,
          ${coords.left - padding}px ${coords.top - padding}px,
          ${coords.left + coords.width + padding}px ${coords.top - padding}px,
          ${coords.left + coords.width + padding}px ${coords.top + coords.height + padding}px,
          ${coords.left - padding}px ${coords.top + coords.height + padding}px,
          ${coords.left - padding}px 100%,
          100% 100%,
          100% 0%)`;
  }
}
